import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { useHideAmounts } from '@/hooks/useHideAmounts';
import { ROUTES } from '@/constants/routes';
import { isAdminOrAbove, isSuperUser as checkSuperUser } from '@/lib/permissions';
import { getColorMode, setColorMode, type ColorMode } from '@/utils/theme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User, LogOut, Settings, EyeOff, Eye, Sun, Moon, Monitor, FileText } from 'lucide-react';
import { GenerateFirmReportDialog } from '@/pages/statistics/GenerateFirmReportDialog';

export function Header({ inline = false }: { inline?: boolean } = {}) {
  const { user, token, logout } = useAuth();
  const { hideAmounts, toggleHideAmounts, saving: hideSaving } = useHideAmounts();
  const navigate = useNavigate();
  const [colorMode, setColorModeState] = useState<ColorMode>(getColorMode());

  const userGroups = user?.groups || [];
  const isAdmin = isAdminOrAbove(userGroups);
  const isSuperUser = checkSuperUser(userGroups);
  const canGenerateFirmReport = isSuperUser || isAdmin;
  const firmId = user?.firms?.[0];
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const handleColorModeChange = (mode: ColorMode) => {
    setColorModeState(mode);
    setColorMode(mode);
  };

  // Listen for system preference changes when in 'system' mode
  useEffect(() => {
    if (colorMode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => setColorMode('system'); // re-apply
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [colorMode]);

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  const handleSettings = () => {
    navigate('/settings');
  };
  
  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`;
  };

  const content = (
        <div className="ml-auto flex items-center gap-2">
          {/* Theme toggle */}
          {token && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  title="Change theme"
                >
                  {colorMode === 'dark' ? (
                    <Moon className="h-4 w-4" />
                  ) : colorMode === 'system' ? (
                    <Monitor className="h-4 w-4" />
                  ) : (
                    <Sun className="h-4 w-4" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleColorModeChange('light')} className="cursor-pointer">
                  <Sun className="mr-2 h-4 w-4" />
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleColorModeChange('dark')} className="cursor-pointer">
                  <Moon className="mr-2 h-4 w-4" />
                  Dark
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleColorModeChange('system')} className="cursor-pointer">
                  <Monitor className="mr-2 h-4 w-4" />
                  System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {token && isAdmin && (
            <div className="flex items-center gap-1">
              <button
                onClick={toggleHideAmounts}
                disabled={hideSaving}
                className="flex h-9 items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors disabled:opacity-50"
                title={hideAmounts ? 'Show amounts' : 'Hide amounts'}
              >
                {hideAmounts ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span className="hidden sm:inline">{hideAmounts ? 'Amounts hidden' : 'Hide amounts'}</span>
              </button>
              {canGenerateFirmReport && firmId && (
                <button
                  onClick={() => setReportDialogOpen(true)}
                  className="flex h-9 items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                  title="Generate Firm Report"
                >
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Firm Report</span>
                </button>
              )}
            </div>
          )}
          {canGenerateFirmReport && firmId && (
            <GenerateFirmReportDialog
              firmId={firmId}
              open={reportDialogOpen}
              onOpenChange={setReportDialogOpen}
            />
          )}
          {token ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative ml-1 h-9 w-9 rounded-full p-0">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src="" alt={`${user?.first_name} ${user?.last_name}`} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center text-center leading-none">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.title || 'User'}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSettings} className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button onClick={() => navigate(ROUTES.LOGIN)}>
              <User className="mr-2 h-4 w-4" />
              Login
            </Button>
          )}
        </div>
  );

  if (inline) return content;

  return (
    <header className="sticky top-0 z-50 w-full bg-background">
      <div className="flex h-16 items-center px-6">{content}</div>
    </header>
  );
}